import React from 'react';
import TextField from '@mui/material/TextField';
import { Autocomplete } from '@mui/material';
import { styled } from '@mui/system';
import { ImageComp } from '../ImageCompo/ImageComp';

const CustomTextField = styled(TextField)(({ theme, paddingTop }) => ({
    '& .MuiOutlinedInput-root': {
        minHeight: '56px',
        borderRadius: '8px',
        backgroundColor: 'white',
        '& fieldset': {
            borderColor: 'gray',
        },
        '&:hover fieldset': {
            borderColor: 'gray',
        },
        '&.Mui-focused fieldset': {
            borderColor: 'gray',
        },
        '&.Mui-error fieldset': {
            borderColor: '#d32f2f !important',
        },
        '&.Mui-error:hover fieldset': {
            borderColor: '#d32f2f !important',
        },
        paddingTop: paddingTop,
    },
    '& .MuiInputLabel-root': {
        color: 'gray',
        '&.Mui-error': {
            color: '#d32f2f !important',
        },
        whiteSpace: 'break-spaces',
        position: 'absolute',
        left: '16px',
        top: '50%',
        transform: 'translateY(-50%) scale(1)',
        transformOrigin: 'left',
        transition: 'all 0.2s ease-in-out',
        lineHeight: '1.5',
        width: 'max-content',
        pointerEvents: 'none',
    },
    '& .MuiInputLabel-root.Mui-focused': {
        color: 'gray',
        top: '-10px',
        transform: 'translateY(0) scale(0.75)',
    },
    '& .MuiInputLabel-shrink': {
        top: '-10px',
        transform: 'translateY(0) scale(0.75)',
    },
    '& .MuiOutlinedInput-input': {
        padding: '12px 16px',
        lineHeight: '1.5',
    },
    '& .MuiFormHelperText-root': {
        marginTop: '4px',
    },
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline legend': {
        whiteSpace: 'pre'
    },
}));

const CustomSelection = ({
    label,
    name,
    required,
    disabled,
    onChange,
    mappingKey,
    value,
    options = [],
    className,
    exDataKey,
    imgKey,
    disabledFilter,
    readOnly,
    sortType = 'alphabetical',
    noSort = false,
    error,
    helperText,
    ...props
}) => {
    const calculatePaddingTop = (label) => {
        const lineCount = label.split('\n').length;
        let basePadding;

        if (label.length >= 800) {
            basePadding = 190;
        } else if (label.length >= 500) {
            basePadding = 130;
        } else if (label.length >= 350) {
            basePadding = 60;
        } else if (label.length >= 200) {
            basePadding = 40;
        } else if (label.length >= 100) {
            basePadding = 20;
        } else {
            basePadding = 10;
        }

        const extraPadding = (lineCount - 1) * 15;
        return `${basePadding + extraPadding}px`;
    };

    const paddingTop = calculatePaddingTop(label);
    const [valueMappingKey, valueExDataKey] = value && typeof value === 'string' ? value.split(' - ') : [];

    const sortedOptions = React.useMemo(() => {
        if (noSort || disabledFilter || options.length === 0) {
            return options;
        }
        
        return [...options].sort((a, b) => {
            const aValue = a[mappingKey] || a.option;
            const bValue = b[mappingKey] || b.option;
            
            if (sortType === 'numerical') {
                return (parseFloat(aValue) || 0) - (parseFloat(bValue) || 0);
            }
            return (aValue?.toString() || '').localeCompare(bValue?.toString() || '');
        });
    }, [options, noSort, disabledFilter, mappingKey, sortType]);

    const selectedOption = React.useMemo(() => {
        return sortedOptions.find((option) => {
            const optionLabel = `${option?.[mappingKey] || option?.option || ''}${
                exDataKey && option?.[exDataKey] ? ` - ${option?.[exDataKey]}` : ''
            }`;
            return optionLabel === value;
        }) || null;
    }, [sortedOptions, value, mappingKey, exDataKey]);

    const filterOptions = React.useCallback((options, { inputValue }) => {
        if (!inputValue.trim()) {
            return options;
        }

        const searchTerms = inputValue.toLowerCase().split(/\s+/).filter(Boolean);
        
        return options.filter((option) => {
            const label = `${option?.[mappingKey] || option?.option}${
                exDataKey && option?.[exDataKey] ? ` - ${option?.[exDataKey]}` : ''
            }`.toLowerCase();

            return searchTerms.every(term => label.includes(term));
        });
    }, [mappingKey, exDataKey]);

    return (
        <Autocomplete
            style={{ width: '100%', zIndex: '88' }}
            options={sortedOptions}
            getOptionLabel={(option) => {
                if (!option) return '';
                return `${option?.[mappingKey] || option?.option || ''}${
                    exDataKey && option?.[exDataKey] ? ` - ${option?.[exDataKey]}` : ''
                }`;
            }}
            renderOption={(props, option, { selected }) => (
                <li
                    {...props}
                    key={`${option?.[mappingKey] || option?.option}-${option?.id || ''}`}
                    style={{
                        backgroundColor: selected ? '#0000ff12' : 'inherit',
                        padding: '8px 16px',
                        whiteSpace: 'normal',
                    }}
                >
                    <div className="flex justify-between w-full items-center">
                        <div className="whitespace-normal break-words text-sm" title={option?.[mappingKey] || option?.option}>
                            {option?.[mappingKey] || option?.option}
                            {exDataKey && option?.[exDataKey] ? ` - ${option?.[exDataKey]}` : ''}
                        </div>
                        {imgKey && option?.[imgKey] && (
                            <div className="ml-4 flex-shrink-0">
                                <ImageComp 
                                    className="h-[30px] w-[30px] object-cover rounded" 
                                    source={option?.[imgKey]} 
                                />
                            </div>
                        )}
                    </div>
                </li>
            )}
            value={selectedOption}
            onChange={(event, newValue) => {
                onChange?.({
                    target: {
                        name,
                        value: newValue
                            ? `${newValue?.[mappingKey] || newValue?.option}${
                                  exDataKey && newValue?.[exDataKey] ? ` - ${newValue?.[exDataKey]}` : ''
                              }`
                            : '',
                    },
                });
            }}
            disabled={disabled || options?.length === 0}
            filterOptions={filterOptions}
            renderInput={(params) => (
                <CustomTextField
                    {...params}
                    label={label}
                    required={required}
                    error={error}
                    helperText={helperText}
                    paddingTop={paddingTop}
                    className={`w-full ${className}`}
                    InputProps={{
                        ...params.InputProps,
                        readOnly: readOnly,
                    }}
                />
            )}
            isOptionEqualToValue={(option, value) => {
                return (option?.[mappingKey] || option?.option) === (value?.[mappingKey] || value?.option);
            }}
            sx={{ 
                minWidth: '200px',
                '& .MuiAutocomplete-listbox': {
                    maxHeight: '300px',
                },
                '& .MuiAutocomplete-option': {
                    minHeight: '48px',
                },
            }}
            noOptionsText="No matching options found"
            loadingText="Loading options..."
            clearOnBlur={true}
            blurOnSelect
        />
    );
};

export default CustomSelection;